import type { Writable } from "svelte/store";
import type { ImagekitConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// ImageKit does not answer other origins. Calls go through /api/imagekit, which
// talks to https://api.imagekit.io. The private key is a request header.

export const prop_imagekit_url = "imagekit_url";
export const prop_imagekit_mime = "imagekit_mime";
export const prop_imagekit_id = "imagekit_id";

class FileSystemImagekit extends FS {
  supportsDirRename = true;

  constructor(config: ImagekitConfig) {
    super(config);
  }

  get privateKey(): string {
    return (this.config as ImagekitConfig).privateKey;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let js = await imagekitCall(this.privateKey, mkURL("/api/imagekit/list", "dir", dirPath));
      if (!Array.isArray(js)) {
        throw new Error("imagekit list failed");
      }
      return FsDir.fromWire(dirPath, js);
    });
  }

  // The address of the file as it was uploaded. For a private file it is
  // signed and works for an hour.
  async getPublicURL(path: string): Promise<string> {
    let js = await imagekitCall(this.privateKey, mkURL("/api/imagekit/link", "path", path));
    throwIf(typeof js.url != "string" || js.url == "", "imagekit gave no link");
    return js.url;
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = mkURL("/api/imagekit/download", "path", path);
    let xhr = await imagekitSend("GET", url, this.privateKey, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  // ImageKit does not store empty files: the server refuses them. A file with
  // the same name is replaced.
  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = mkURL("/api/imagekit/upload", "path", path);
    await imagekitSend("PUT", url, this.privateKey, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = mkURL("/api/imagekit/rename", "path", path, "newPath", newPath);
    await imagekitCall(this.privateKey, url, "POST");
  }

  async createDirectory(dirPath: string): Promise<void> {
    await imagekitCall(this.privateKey, mkURL("/api/imagekit/mkdir", "path", dirPath), "POST");
  }

  // A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    await imagekitCall(this.privateKey, mkURL("/api/imagekit/delete", "path", path), "POST");
  }
}

export async function testImagekitConnection(privateKey: string): Promise<void> {
  await imagekitCall(privateKey, "/api/imagekit/test");
}

async function imagekitCall(privateKey: string, url: string, method = "GET"): Promise<any> {
  let rsp = await fetch(url, {
    method,
    headers: { "X-Imagekit-Key": privateKey },
  });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `imagekit ${rsp.status}`);
  }
  return js;
}

function imagekitSend(
  method: string,
  url: string,
  privateKey: string,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    xhr.setRequestHeader("X-Imagekit-Key", privateKey);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      imagekitErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`imagekit ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("imagekit request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function imagekitErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `imagekit ${xhr.status}`;
}

export function makeFileSystemImagekit(config: ImagekitConfig): FileSystemImagekit {
  return new FileSystemImagekit(config);
}
